// ============================================================================
// OTA SERVICE
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { otaEndpoints } from '../endpoints/ota.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  Ota,
  CreateOtaRequest,
  UpdateOtaRequest,
  OtaSearchRequest,
} from '../types';

class OtaService {
  async getAll(params?: OtaSearchRequest): Promise<PaginatedResponse<Ota>> {
    const endpoint = params
      ? buildEndpointWithQuery('/Ota', params)
      : otaEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw.data ?? {};

    return {
      success: raw.success,
      message: raw.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<Ota>> {
    const response = await get<ApiResponse<Ota>>(otaEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<Ota>> {
    const response = await get<ApiResponse<Ota>>(otaEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateOtaRequest): Promise<ApiResponse<Ota>> {
    const response = await post<ApiResponse<Ota>>(otaEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateOtaRequest): Promise<ApiResponse<Ota>> {
    const response = await put<ApiResponse<Ota>>(otaEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(otaEndpoints.delete(id), {
      params: { rowVersion },
    });
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    let endpoint = otaEndpoints.validateCode(code);
    if (excludeId) {
      endpoint = `${endpoint}?excludeId=${excludeId}`;
    }
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const otaService = new OtaService();
export default otaService;
